// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the web form review card's body. A screenshot of the form as the Assistant filled
// it in, every field and value, and after Confirm the page the site answered with.
import styles from '@app/features/konvo_assistant/components/AssistantActionCard.module.css';
import {http} from '@app/features/platform/transport/RestTransport';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import type {AssistantActionDraft} from '@konvo/schema/src/domains/konvo/AssistantActionDraft';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {useEffect, useId, useState} from 'react';

export type WebFormDraft = Extract<AssistantActionDraft, {kind: 'web_form'}>;

const shotCache = new Map<string, string>();

function siteName(url: string): string {
	try {
		return new URL(url).host.replace(/^www\./, '');
	} catch {
		return url;
	}
}

function WebShot({id, alt}: {id: string; alt: string}) {
	const {t} = useLingui();
	const [src, setSrc] = useState(() => shotCache.get(id) ?? '');
	const [failed, setFailed] = useState(false);
	const [expanded, setExpanded] = useState(false);
	useEffect(() => {
		if (shotCache.has(id)) return;
		let cancelled = false;
		void http
			.get<{data_url: string}>(`/konvo/web-shots/${encodeURIComponent(id)}`)
			.then((response) => {
				shotCache.set(id, response.body.data_url);
				if (!cancelled) setSrc(response.body.data_url);
			})
			.catch(() => {
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [id]);
	if (failed) return <p className={styles.shotMissing}>{t`The screenshot is no longer available.`}</p>;
	return (
		<button
			type="button"
			className={clsx(styles.shot, expanded && styles.shotExpanded)}
			aria-expanded={expanded}
			aria-label={expanded ? t`Show less of the screenshot` : t`Show the whole screenshot`}
			onClick={() => setExpanded((value) => !value)}
		>
			{src ? <img src={src} alt={alt} /> : <span className={styles.shotLoading}>{t`Loading screenshot…`}</span>}
		</button>
	);
}

function SiteLink({url, label}: {url: string; label: string}) {
	return (
		<a
			className={styles.noteLink}
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			title={url}
			onClick={(event) => {
				event.preventDefault();
				void openExternalUrl(url);
			}}
		>
			{label}
		</a>
	);
}

export function WebFormSummary({draft, edited}: {draft: WebFormDraft; edited: boolean}) {
	const {t} = useLingui();
	const site = siteName(draft.url);
	const submitLabel = draft.submitLabel;
	return (
		<>
			<span className={styles.title}>{draft.title || site}</span>
			<span className={styles.meta}>
				<SiteLink url={draft.url} label={site} /> · {t`Sends with “${submitLabel}”`}
			</span>
			<WebShot id={draft.screenshotId} alt={t`The form on ${site}, filled in`} />
			{edited && <span className={styles.meta}>{t`The screenshot shows the form before your edits.`}</span>}
			<dl className={styles.formFields} data-flx="konvo-assistant.action-card.web-fields">
				{draft.fields.map((field) => (
					<div key={field.ref} className={styles.formField}>
						<dt>
							{field.label}
							{field.required ? ' *' : ''}
						</dt>
						<dd className={clsx(!field.value && styles.formFieldEmpty)}>
							{field.sensitive ? t`Left for you to fill in on the site` : field.value || t`Empty`}
						</dd>
					</div>
				))}
			</dl>
			{draft.result && (
				<div className={styles.formResult} data-flx="konvo-assistant.action-card.web-result">
					<span className={styles.meta}>
						{t`What the site showed after sending:`}{' '}
						<SiteLink url={draft.result.url} label={draft.result.title || siteName(draft.result.url)} />
					</span>
					{draft.result.screenshotId && (
						<WebShot id={draft.result.screenshotId} alt={t`The page after sending the form`} />
					)}
				</div>
			)}
		</>
	);
}

export function WebFormFields({
	draft,
	onChange,
}: {
	draft: WebFormDraft;
	onChange: (fields: WebFormDraft['fields']) => void;
}) {
	const base = useId();
	return (
		<>
			{draft.fields
				.filter((field) => field.editable)
				.map((field) => {
					const set = (value: string) =>
						onChange(draft.fields.map((other) => (other.ref === field.ref ? {...other, value} : other)));
					const id = `${base}-${field.ref}`;
					const long = field.value.length > 60 || field.value.includes('\n');
					return (
						<div key={field.ref} className={styles.webField}>
							<label htmlFor={id}>{field.label}</label>
							{long ? (
								<textarea id={id} rows={4} value={field.value} onChange={(e) => set(e.target.value)} />
							) : (
								<input id={id} value={field.value} onChange={(e) => set(e.target.value)} />
							)}
						</div>
					);
				})}
		</>
	);
}
